import { createClient } from '@supabase/supabase-js'

// Pick Supabase project based on hostname so this works without env vars on Netlify
const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const isProd = hostname === 'menuron-admin-prod.prismatic-a.com'

const PROD_URL  = 'https://rxphoguizrdczsaiteon.supabase.co'
const PROD_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ4cGhvZ3VpenJkY3pzYWl0ZW9uIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTgxOTkyMjcsImV4cCI6MjA3Mzc3NTIyN30.UXUwlCPQDZXriYZ4VyKINczTvEdh0_D8h-DPxQvwlLY'
const DEV_URL   = 'https://sfgufwlolnoiehwpnomv.supabase.co'
const DEV_ANON  = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNmZ3Vmd2xvbG5vaWVod3Bub212Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTc1MDgzNzEsImV4cCI6MjA3MzA4NDM3MX0.bM5jzyizn5UwHKIXALGWR-1GEkkgBJGa7yVWu-ckksw'

const supabaseUrl  = isProd ? PROD_URL  : DEV_URL
const supabaseAnonKey = isProd ? PROD_ANON : DEV_ANON

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Hash password function (same as main platform)
export const hashPassword = async (password) => {
  try {
    const encoder = new TextEncoder();
    const saltedPassword = password + 'menuron_salt_2024';
    const data = encoder.encode(saltedPassword);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Password hashing failed:', error);
    throw new Error('Password processing failed');
  }
};

// Admin Services — all DB operations go through the admin-api Edge Function
// so the service role key never touches the browser and every request is
// validated against a real admin session server-side.
export const adminService = {

  // Internal helper — calls the Edge Function with the current session token
  async _call(action, params = {}) {
    const sessionToken = localStorage.getItem('menuron_admin_session');
    const res = await fetch(`${supabaseUrl}/functions/v1/admin-api`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': supabaseAnonKey,
        'Authorization': `Bearer ${supabaseAnonKey}`,
        'x-admin-session': sessionToken || '',
      },
      body: JSON.stringify({ action, ...params }),
    });
    const json = await res.json();
    if (!res.ok) return { data: null, error: { message: json.error || 'Request failed' } };
    return { data: json.data, error: null };
  },

  async loginAdmin(email, password) {
    return this._call('login', { email, password });
  },

  async verifySession(sessionToken) {
    return this._call('verifySession', { sessionToken });
  },

  async logout(sessionToken) {
    // Pass token explicitly via header by temporarily writing to localStorage before _call
    localStorage.setItem('menuron_admin_session', sessionToken || '');
    const result = await this._call('logout');
    await supabase.auth.signOut().catch(() => {});
    return result;
  },

  async changePassword(adminId, currentPassword, newPassword) {
    return this._call('changePassword', { adminId, currentPassword, newPassword });
  },

  async getAllRestaurants() {
    return this._call('getAllRestaurants');
  },

  async getDashboardStats() {
    return this._call('getDashboardStats');
  },

  async createRestaurant(restaurantData, ownerData) {
    return this._call('createRestaurant', { restaurantData, ownerData });
  },

  async toggleRestaurantStatus(restaurantId, isActive, reason = '') {
    return this._call('toggleRestaurantStatus', { restaurantId, isActive, reason });
  },

  async updatePosEnabled(restaurantId, enabled) {
    return this._call('updatePosEnabled', { restaurantId, enabled });
  },

  async updateQrAppEnabled(restaurantId, enabled) {
    return this._call('updateQrAppEnabled', { restaurantId, enabled });
  },

  async resetOwnerPassword(restaurantId, newPassword) {
    return this._call('resetOwnerPassword', { restaurantId, newPassword });
  },

  async updateRestaurant(restaurantId, updates) {
    return this._call('updateRestaurant', { restaurantId, updates });
  },

  async updateOwnerProfile(restaurantId, { fullName, email, newPassword }) {
    return this._call('updateOwnerProfile', { restaurantId, fullName, email, newPassword });
  },

  async uploadRestaurantLogo(file, restaurantId) {
    const fileExt = file.name.split('.').pop();
    const { data, error } = await this._call('getRestaurantLogoUploadUrl', { restaurantId, extension: fileExt });
    if (error || !data) return { url: null, error: error || { message: 'Failed to get upload URL' } };
    const { error: uploadError } = await supabase.storage
      .from('restaurant-logos')
      .uploadToSignedUrl(data.path, data.token, file);
    if (uploadError) return { url: null, error: uploadError };
    return { url: data.publicUrl, error: null };
  },

  // ── Restaurant Management ────────────────────────────────────────────────

  async getRestaurantCategories(restaurantId) {
    return this._call('getRestaurantCategories', { restaurantId });
  },
  async createCategory(restaurantId, name, displayOrder) {
    return this._call('createCategory', { restaurantId, name, displayOrder });
  },
  async updateCategory(categoryId, updates) {
    return this._call('updateCategory', { categoryId, updates });
  },
  async deleteCategory(categoryId) {
    return this._call('deleteCategory', { categoryId });
  },

  async getMenuItems(restaurantId) {
    return this._call('getMenuItems', { restaurantId });
  },
  async createMenuItem(data) {
    return this._call('createMenuItem', { data });
  },
  async updateMenuItem(itemId, updates) {
    return this._call('updateMenuItem', { itemId, updates });
  },
  async deleteMenuItem(itemId) {
    return this._call('deleteMenuItem', { itemId });
  },

  async getRestaurantTables(restaurantId) {
    return this._call('getRestaurantTables', { restaurantId });
  },
  async createTable(restaurantId, data) {
    return this._call('createTable', { restaurantId, data });
  },
  async updateTable(tableId, updates) {
    return this._call('updateTable', { tableId, updates });
  },
  async deleteTable(tableId) {
    return this._call('deleteTable', { tableId });
  },

  async getRestaurantOrders(restaurantId) {
    return this._call('getRestaurantOrders', { restaurantId });
  },

  async getRestaurantWaiters(restaurantId) {
    return this._call('getRestaurantWaiters', { restaurantId });
  },
  async createWaiter(restaurantId, data) {
    return this._call('createWaiter', { restaurantId, data });
  },
  async updateWaiter(waiterId, data) {
    return this._call('updateWaiter', { waiterId, data });
  },
  async deleteWaiter(waiterId) {
    return this._call('deleteWaiter', { waiterId });
  },

  // Image upload — gets a signed upload URL from Edge Function (service role),
  // then uploads directly to storage without needing a Supabase Auth session.
  async uploadMenuItemImage(file, restaurantId) {
    const fileExt = file.name.split('.').pop();
    const { data, error } = await this._call('getMenuImageUploadUrl', { restaurantId, extension: fileExt });
    if (error || !data) return { url: null, error: error || { message: 'Failed to get upload URL' } };
    const { error: uploadError } = await supabase.storage
      .from('menu-images')
      .uploadToSignedUrl(data.path, data.token, file);
    if (uploadError) return { url: null, error: uploadError };
    return { url: data.publicUrl, error: null };
  },
  async deleteMenuItemImage(imageUrl) {
    const path = imageUrl.split('/menu-images/')[1];
    if (path) await supabase.storage.from('menu-images').remove([path]);
  },
};

export const analyticsService = {
  getOrdersForPeriod: (restaurantId, startDate, endDate) =>
    adminService._call('getOrdersForAnalytics', {
      restaurantId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }),
};

export const posService = {
  getInvoices: (restaurantId) => adminService._call('getInvoices', { restaurantId }),
  markOrderInvalid: (orderId) => adminService._call('markOrderInvalid', { orderId }),
  updatePaymentMethod: (orderId, method) => adminService._call('updatePaymentMethod', { orderId, method }),
  getOrdersForReport: (restaurantId, startDate, endDate) =>
    adminService._call('getOrdersForReport', { restaurantId, startDate, endDate }),
  getManagerActivityReport: (restaurantId, startDate, endDate) =>
    adminService._call('getManagerActivityReport', { restaurantId, startDate, endDate }),
};

export const billingService = {
  getSettings: (restaurantId) => adminService._call('getBillingSettings', { restaurantId }),
  saveSettings: (restaurantId, settings) => adminService._call('saveBillingSettings', { restaurantId, settings }),
  getBillingDefaults: async () => ({ data: null, error: null }),
  normalizeBillingSettings: (s) => s,
};

export const crmService = {
  getCustomers:      (restaurantId)        => adminService._call('getCustomers',      { restaurantId }),
  createCustomer:    (restaurantId, data)  => adminService._call('createCustomer',    { restaurantId, data }),
  updateCustomer:    (customerId, fields)  => adminService._call('updateCustomer',    { customerId, fields }),
  deleteCustomer:    (customerId)          => adminService._call('deleteCustomer',    { customerId }),
  getCustomerOrders: (customerId)          => adminService._call('getCustomerOrders', { customerId }),
};

export const inventoryService = {
  getSummary:    (restaurantId)         => adminService._call('getInventorySummary',    { restaurantId }),
  getItems:      (restaurantId)         => adminService._call('getInventoryItems',       { restaurantId }),
  getCategories: (restaurantId)         => adminService._call('getInventoryCategories',  { restaurantId }),
  getUnits:      (restaurantId)         => adminService._call('getInventoryUnits',       { restaurantId }),
  getSuppliers:      (restaurantId)              => adminService._call('getInventorySuppliers', { restaurantId }),
  updateItem:        (itemId, updates)           => adminService._call('updateInventoryItem',   { itemId, updates }),
  createItem:        (restaurantId, item)        => adminService._call('createInventoryItem',   { restaurantId, item }),
  deleteItem:        (itemId)                    => adminService._call('deleteInventoryItem',   { itemId }),
  setOpeningStock:   (restaurantId, itemId, qty, value) => adminService._call('setOpeningStock', { restaurantId, itemId, qty, value }),
};

// Stubs — dataService.js re-exports these; not used in admin portal
export const orderService      = {};
export const restaurantService = {};
export const menuService       = {};
export const authService       = {};